import React from 'react';
import Icon from '../../../components/AppIcon';

const ArticleCard = ({ article, isAdmin, onEdit, onDelete, onClick }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTopic = (topic) => {
    return topic?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase());
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published':
        return 'bg-success/10 text-success';
      case 'draft':
        return 'bg-warning/10 text-warning';
      case 'archived':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow group">
      {/* Featured Image */}
      {article?.featuredImageUrl ? (
        <div className="aspect-video overflow-hidden bg-muted relative">
          <img
            src={article?.featuredImageUrl}
            alt={article?.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          {/* Labels Overlay */}
          <div className="absolute top-12 right-12 flex flex-col gap-8">
            {article?.featured && (
              <span className="inline-flex items-center gap-4 px-10 py-6 bg-yellow-500 text-white text-xs font-bold rounded shadow-lg">
                <Icon name="Star" size={14} />
                FEATURED
              </span>
            )}
            {article?.comingSoon && (
              <span className="inline-flex items-center gap-4 px-10 py-6 bg-blue-500 text-white text-xs font-bold rounded shadow-lg">
                <Icon name="Clock" size={14} />
                COMING SOON
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="aspect-video bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center relative">
          <Icon name="FileText" size={48} className="text-muted-foreground" />
          {/* Labels Overlay */}
          <div className="absolute top-12 right-12 flex flex-col gap-8">
            {article?.featured && (
              <span className="inline-flex items-center gap-4 px-10 py-6 bg-yellow-500 text-white text-xs font-bold rounded shadow-lg">
                <Icon name="Star" size={14} />
                FEATURED
              </span>
            )}
            {article?.comingSoon && (
              <span className="inline-flex items-center gap-4 px-10 py-6 bg-blue-500 text-white text-xs font-bold rounded shadow-lg">
                <Icon name="Clock" size={14} />
                COMING SOON
              </span>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-20">
        {/* Topic & Status */}
        <div className="flex items-center gap-8 mb-12">
          <span className="inline-flex items-center px-8 py-4 bg-primary/10 text-primary text-xs font-medium rounded">
            {formatTopic(article?.topic)}
          </span>
          {isAdmin && article?.status !== 'published' && (
            <span className={`inline-flex items-center px-8 py-4 text-xs font-medium rounded ${getStatusColor(article?.status)}`}>
              {article?.status?.charAt(0)?.toUpperCase() + article?.status?.slice(1)}
            </span>
          )}
        </div>

        {/* Title */}
        <h3
          className="text-lg font-semibold text-foreground mb-8 line-clamp-2 cursor-pointer hover:text-primary transition-colors"
          onClick={onClick}
        >
          {article?.title}
        </h3>

        {/* Excerpt */}
        <p className="text-sm text-muted-foreground mb-16 line-clamp-3">
          {article?.excerpt}
        </p>

        {/* Metadata */}
        <div className="flex items-center gap-16 text-xs text-muted-foreground mb-16">
          <div className="flex items-center gap-4">
            <Icon name="Calendar" size={14} />
            <span>{formatDate(article?.publishedAt || article?.createdAt)}</span>
          </div>
          {article?.readingTimeMinutes && (
            <div className="flex items-center gap-4">
              <Icon name="Clock" size={14} />
              <span>{article?.readingTimeMinutes} min read</span>
            </div>
          )}
          {article?.viewCount > 0 && (
            <div className="flex items-center gap-4">
              <Icon name="Eye" size={14} />
              <span>{article?.viewCount} views</span>
            </div>
          )}
        </div>

        {/* Author */}
        {article?.author && (
          <div className="flex items-center gap-8 mb-16">
            {article?.author?.avatarUrl ? (
              <img
                src={article?.author?.avatarUrl}
                alt={article?.author?.fullName}
                className="w-6 h-6 rounded-full"
              />
            ) : (
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="User" size={12} className="text-primary" />
              </div>
            )}
            <span className="text-xs text-muted-foreground">
              {article?.author?.fullName || 'Rovyn Group'}
            </span>
          </div>
        )}

        {/* Tags */}
        {article?.tags && article?.tags?.length > 0 && (
          <div className="flex flex-wrap gap-8 mb-16">
            {article?.tags?.slice(0, 3)?.map((tag) => (
              <span
                key={tag?.id}
                className="inline-flex items-center px-8 py-4 bg-muted text-muted-foreground text-xs rounded"
              >
                #{tag?.tagName}
              </span>
            ))}
            {article?.tags?.length > 3 && (
              <span className="text-xs text-muted-foreground">+{article?.tags?.length - 3} more</span>
            )}
          </div>
        )}

        {/* Admin Actions */}
        {isAdmin && (
          <div className="flex gap-8 pt-16 border-t border-border">
            <button
              onClick={(e) => {
                e?.stopPropagation();
                onEdit();
              }}
              className="flex-1 flex items-center justify-center gap-8 px-12 py-8 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm font-medium"
            >
              <Icon name="Edit" size={16} />
              Edit
            </button>
            <button
              onClick={(e) => {
                e?.stopPropagation();
                onDelete();
              }}
              className="flex-1 flex items-center justify-center gap-8 px-12 py-8 bg-destructive/10 text-destructive rounded-lg hover:bg-destructive/20 transition-colors text-sm font-medium"
            >
              <Icon name="Trash2" size={16} />
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleCard;